/**
 * Google Pull Sync Persistence Helper
 * 
 * Applies and persists pull results from Google Calendar to AX1 events.
 * Handles updates, cancellations, and conflicts.
 * 
 * Key Rules:
 * - Only apply allowed partial update fields (provided by service)
 * - Cancelled events get status:'cancelled' (not deleted)
 * - Conflicts get hasPendingExternalChanges=true with summary in externalSourceData
 * - Updates externalLastModified to prevent infinite push-pull loops
 * 
 * @module calendar/integrations/applyGooglePullResults
 * @version 1.0.0
 */

import { CalendarEventService } from '../services/CalendarEventService';
import type { CalendarEvent, ExternalSyncInfo } from '../types';
import type { PullChangesResult, PullEventResult, SyncConflict } from './GoogleCalendarSyncService';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Result of applying a single pull update.
 */
export interface ApplyPullResult {
  /** AX1 event ID */
  ax1EventId: string;
  /** Whether apply succeeded */
  success: boolean;
  /** Operation performed */
  operation: 'updated' | 'cancelled' | 'conflict_flagged' | 'skipped' | 'error';
  /** Fields that were updated */
  updatedFields?: string[];
  /** Error message if failed */
  error?: string;
  /** Timestamp */
  timestamp: string;
}

/**
 * Result of applying all pull results.
 */
export interface ApplyPullBatchResult {
  /** Total results processed */
  total: number;
  /** Successfully applied updates */
  updated: number;
  /** Events marked as cancelled */
  cancelled: number;
  /** Events flagged with conflicts */
  conflictsFlagged: number;
  /** Skipped (no changes needed) */
  skipped: number;
  /** Errors encountered */
  errors: number;
  /** Individual results */
  results: ApplyPullResult[];
  /** Timestamp */
  timestamp: string;
}

/**
 * Conflict summary stored in externalSourceData.
 */
export interface ConflictSummary {
  /** When conflict was detected */
  detectedAt: string;
  /** Number of conflicts */
  count: number;
  /** Conflict details */
  conflicts: Array<{
    type: string;
    field?: string;
    ax1Value?: unknown;
    googleValue?: unknown;
    description: string;
    recommendedAction: string;
  }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Pull Log (in-memory for debugging)
// ─────────────────────────────────────────────────────────────────────────────

interface PullLogEntry {
  timestamp: string;
  ax1EventId: string;
  operation: string;
  success: boolean;
  error?: string;
}

const pullLog: PullLogEntry[] = [];
const MAX_LOG_ENTRIES = 100;

function logPull(entry: Omit<PullLogEntry, 'timestamp'>): void {
  pullLog.unshift({
    ...entry,
    timestamp: new Date().toISOString(),
  });
  
  if (pullLog.length > MAX_LOG_ENTRIES) {
    pullLog.length = MAX_LOG_ENTRIES;
  }
}

/**
 * Get recent pull log entries.
 */
export function getPullSyncLog(limit = 50): PullLogEntry[] {
  return pullLog.slice(0, limit);
}

// ─────────────────────────────────────────────────────────────────────────────
// Allowed Fields for Pull Updates
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fields that are allowed to be updated from Google Calendar.
 * These are non-AX1-owned fields (time, title, description, location).
 * 
 * NEVER updated from Google:
 * - project, status (except cancelled), assignee, owner
 * - priority, progress, attendees (AX1-owned)
 */
const ALLOWED_PULL_FIELDS = new Set([
  'title',
  'description',
  'date',
  'time',
  'duration',
  'location',
]);

/**
 * Filters a partial update to only include allowed fields.
 */
function filterAllowedFields(
  update: Partial<CalendarEvent>
): Partial<CalendarEvent> {
  const filtered: Partial<CalendarEvent> = {};
  
  for (const [key, value] of Object.entries(update)) {
    if (ALLOWED_PULL_FIELDS.has(key)) {
      (filtered as Record<string, unknown>)[key] = value;
    }
  }
  
  return filtered;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Helper
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Applies and persists Google Calendar pull results to AX1 events.
 * 
 * Behavior:
 * - For 'updated' results: applies allowed partial fields, updates externalLastModified
 * - For 'cancelled' results: sets status:'cancelled' (does NOT delete)
 * - For 'conflict' results: sets hasPendingExternalChanges=true, stores conflict summary
 * - Updates externalSync.externalLastModified to prevent push-back loops
 * 
 * @param pullResult - Result from pullGoogleCalendarChanges()
 * @param currentEventsById - Map of current AX1 events by ID (for reference)
 * @returns Batch result with all apply operations
 * 
 * @example
 * const pullResult = await pullGoogleCalendarChanges(syncedEvents);
 * if (pullResult.success && pullResult.data) {
 *   const eventsById = new Map(events.map(e => [e.id, e]));
 *   await applyAndPersistGooglePullResults(pullResult.data, eventsById);
 * }
 */
export async function applyAndPersistGooglePullResults(
  pullResult: PullChangesResult,
  currentEventsById: Map<string, CalendarEvent>
): Promise<ApplyPullBatchResult> {
  const timestamp = new Date().toISOString();
  const service = CalendarEventService.getInstance();
  
  const results: ApplyPullResult[] = [];
  let updated = 0;
  let cancelled = 0;
  let conflictsFlagged = 0;
  let skipped = 0;
  let errors = 0;

  for (const pullEventResult of pullResult.results) {
    const result = await applySinglePullResult(
      pullEventResult,
      currentEventsById,
      service
    );
    
    results.push(result);
    
    switch (result.operation) {
      case 'updated':
        updated++;
        break;
      case 'cancelled':
        cancelled++;
        break;
      case 'conflict_flagged':
        conflictsFlagged++;
        break;
      case 'skipped':
        skipped++;
        break;
      case 'error':
        errors++;
        break;
    }
  }

  return {
    total: pullResult.results.length,
    updated,
    cancelled,
    conflictsFlagged,
    skipped,
    errors,
    results,
    timestamp,
  };
}

/**
 * Applies a single pull event result.
 */
async function applySinglePullResult(
  pullEventResult: PullEventResult,
  currentEventsById: Map<string, CalendarEvent>,
  service: CalendarEventService
): Promise<ApplyPullResult> {
  const timestamp = new Date().toISOString();
  const { ax1EventId, googleEventId, operation, partialUpdate, conflicts } = pullEventResult;

  // Get current event state
  const currentEvent = currentEventsById.get(ax1EventId);
  if (!currentEvent) {
    const error = `Event ${ax1EventId} not found in current events map`;
    logPull({ ax1EventId, operation: 'error', success: false, error });
    return {
      ax1EventId,
      success: false,
      operation: 'error',
      error,
      timestamp,
    };
  }

  const currentSync = currentEvent.externalSync;
  if (!currentSync) {
    const error = `Event ${ax1EventId} has no external sync info`;
    logPull({ ax1EventId, operation: 'error', success: false, error });
    return {
      ax1EventId,
      success: false,
      operation: 'error',
      error,
      timestamp,
    };
  }

  try {
    switch (operation) {
      case 'updated':
        return await applyUpdate(
          ax1EventId,
          googleEventId,
          partialUpdate,
          currentSync,
          service,
          timestamp
        );

      case 'cancelled':
        return await applyCancellation(
          ax1EventId,
          googleEventId,
          currentSync,
          service,
          timestamp
        );

      case 'conflict':
        return await applyConflictFlag(
          ax1EventId,
          googleEventId,
          conflicts ?? [],
          currentSync,
          service,
          timestamp
        );

      case 'skipped':
        logPull({ ax1EventId, operation: 'skipped', success: true });
        return {
          ax1EventId,
          success: true,
          operation: 'skipped',
          timestamp,
        };

      case 'error':
        logPull({ 
          ax1EventId, 
          operation: 'error', 
          success: false, 
          error: pullEventResult.message 
        });
        return {
          ax1EventId,
          success: false,
          operation: 'error',
          error: pullEventResult.message,
          timestamp,
        };

      default:
        return {
          ax1EventId,
          success: true,
          operation: 'skipped',
          timestamp,
        };
    }
  } catch (err) {
    const error = err instanceof Error ? err.message : 'Unknown error';
    logPull({ ax1EventId, operation: 'error', success: false, error });
    
    console.error('[applyAndPersistGooglePullResults] Error applying pull result:', {
      ax1EventId,
      operation,
      error,
    });
    
    return {
      ax1EventId,
      success: false,
      operation: 'error',
      error,
      timestamp,
    };
  }
}

/**
 * Applies an update from Google to AX1 event.
 * Updates externalLastModified to prevent infinite push-pull loops.
 */
async function applyUpdate(
  ax1EventId: string,
  googleEventId: string,
  partialUpdate: Partial<CalendarEvent> | undefined,
  currentSync: ExternalSyncInfo,
  service: CalendarEventService,
  timestamp: string
): Promise<ApplyPullResult> {
  if (!partialUpdate || Object.keys(partialUpdate).length === 0) {
    logPull({ ax1EventId, operation: 'skipped', success: true });
    return {
      ax1EventId,
      success: true,
      operation: 'skipped',
      timestamp,
    };
  }

  // Filter to only allowed fields
  const filteredUpdate = filterAllowedFields(partialUpdate);
  const updatedFields = Object.keys(filteredUpdate);

  if (updatedFields.length === 0) {
    logPull({ ax1EventId, operation: 'skipped', success: true });
    return {
      ax1EventId,
      success: true,
      operation: 'skipped',
      timestamp,
    };
  }

  // Build updated sync info with new externalLastModified and lastAppliedFromExternalAt
  // lastAppliedFromExternalAt prevents the next push from sending these changes back to Google
  const updatedSyncInfo: ExternalSyncInfo = {
    ...currentSync,
    lastSyncedAt: timestamp,
    externalLastModified: timestamp, // Mark as applied from Google
    lastAppliedFromExternalAt: timestamp, // Loop prevention: push will skip this event for a short window
    syncStatus: 'synced',
    hasPendingExternalChanges: false,
    syncError: undefined,
  };

  // Apply the update
  const result = await service.applyPullSyncUpdate(
    ax1EventId,
    filteredUpdate,
    updatedSyncInfo
  );

  if (!result.success) {
    const error = result.error?.message ?? 'Failed to apply update';
    logPull({ ax1EventId, operation: 'updated', success: false, error });
    return {
      ax1EventId,
      success: false,
      operation: 'error',
      error,
      timestamp,
    };
  }

  logPull({ ax1EventId, operation: 'updated', success: true });
  return {
    ax1EventId,
    success: true,
    operation: 'updated',
    updatedFields,
    timestamp,
  };
}

/**
 * Applies cancellation from Google.
 * Sets status:'cancelled' but does NOT delete the event.
 */
async function applyCancellation(
  ax1EventId: string,
  googleEventId: string,
  currentSync: ExternalSyncInfo,
  service: CalendarEventService,
  timestamp: string
): Promise<ApplyPullResult> {
  // Build updated sync info with lastAppliedFromExternalAt for loop prevention
  const updatedSyncInfo: ExternalSyncInfo = {
    ...currentSync,
    lastSyncedAt: timestamp,
    externalLastModified: timestamp,
    lastAppliedFromExternalAt: timestamp, // Loop prevention
    syncStatus: 'synced',
    hasPendingExternalChanges: false,
    syncError: undefined,
  };

  // Set status to cancelled
  const result = await service.applyPullSyncUpdate(
    ax1EventId,
    { status: 'cancelled' },
    updatedSyncInfo,
    { 
      googleCancelledAt: timestamp,
      cancelledFromGoogle: true,
    }
  );

  if (!result.success) {
    const error = result.error?.message ?? 'Failed to apply cancellation';
    logPull({ ax1EventId, operation: 'cancelled', success: false, error });
    return {
      ax1EventId,
      success: false,
      operation: 'error',
      error,
      timestamp,
    };
  }

  logPull({ ax1EventId, operation: 'cancelled', success: true });
  return {
    ax1EventId,
    success: true,
    operation: 'cancelled',
    updatedFields: ['status'],
    timestamp,
  };
}

/**
 * Applies conflict flag to event.
 * Sets hasPendingExternalChanges=true and stores conflict summary.
 */
async function applyConflictFlag(
  ax1EventId: string,
  googleEventId: string,
  conflicts: SyncConflict[],
  currentSync: ExternalSyncInfo,
  service: CalendarEventService,
  timestamp: string
): Promise<ApplyPullResult> {
  // Build updated sync info with conflict flag
  const updatedSyncInfo: ExternalSyncInfo = {
    ...currentSync,
    lastSyncedAt: timestamp,
    syncStatus: 'conflict',
    hasPendingExternalChanges: true,
    // Don't update externalLastModified so we know which version has conflicts
  };

  // Build conflict summary for externalSourceData
  const conflictSummary: ConflictSummary = {
    detectedAt: timestamp,
    count: conflicts.length,
    conflicts: conflicts.map(c => ({
      type: c.type,
      field: c.field,
      ax1Value: c.ax1Value,
      googleValue: c.googleValue,
      description: c.description,
      recommendedAction: c.recommendedAction,
    })),
  };

  // Apply the conflict flag
  const result = await service.applyPullSyncUpdate(
    ax1EventId,
    {}, // No field updates - just flag the conflict
    updatedSyncInfo,
    { conflictSummary }
  );

  if (!result.success) {
    const error = result.error?.message ?? 'Failed to apply conflict flag';
    logPull({ ax1EventId, operation: 'conflict_flagged', success: false, error });
    return {
      ax1EventId,
      success: false,
      operation: 'error',
      error,
      timestamp,
    };
  }

  logPull({ ax1EventId, operation: 'conflict_flagged', success: true });
  return {
    ax1EventId,
    success: true,
    operation: 'conflict_flagged',
    timestamp,
  };
}

/**
 * Clears conflict flag after user resolves conflicts.
 * Call this after user reviews and accepts/rejects changes.
 */
export async function clearConflictFlag(
  ax1EventId: string,
  keepGoogleChanges: boolean
): Promise<ApplyPullResult> {
  const timestamp = new Date().toISOString();
  const service = CalendarEventService.getInstance();

  try {
    // Get current event
    const eventResult = await service.getEventById(ax1EventId);
    if (!eventResult.success || !eventResult.data) {
      return {
        ax1EventId,
        success: false,
        operation: 'error',
        error: 'Event not found',
        timestamp,
      };
    }

    const currentSync = eventResult.data.externalSync;
    if (!currentSync) {
      return {
        ax1EventId,
        success: false,
        operation: 'error',
        error: 'No external sync info',
        timestamp,
      };
    }

    // Build cleared sync info
    const clearedSyncInfo: ExternalSyncInfo = {
      ...currentSync,
      lastSyncedAt: timestamp,
      externalLastModified: keepGoogleChanges ? timestamp : currentSync.externalLastModified,
      syncStatus: 'synced',
      hasPendingExternalChanges: false,
      syncError: undefined,
    };

    // Clear conflict summary from source data
    const result = await service.applyPullSyncUpdate(
      ax1EventId,
      {},
      clearedSyncInfo,
      { 
        conflictSummary: null, // Clear conflict summary
        conflictResolvedAt: timestamp,
        conflictResolution: keepGoogleChanges ? 'accepted_google' : 'rejected_google',
      }
    );

    if (!result.success) {
      return {
        ax1EventId,
        success: false,
        operation: 'error',
        error: result.error?.message ?? 'Failed to clear conflict',
        timestamp,
      };
    }

    return {
      ax1EventId,
      success: true,
      operation: 'updated',
      timestamp,
    };
  } catch (err) {
    return {
      ax1EventId,
      success: false,
      operation: 'error',
      error: err instanceof Error ? err.message : 'Unknown error',
      timestamp,
    };
  }
}

export default applyAndPersistGooglePullResults;
